import React from "react";
import { AlertCircle } from "lucide-react";
import { Layout } from "@/components/Layout";
import { LEGAL_DOCS } from "@/content";

// Parse the flat policy text into sections: any line like "1. Heading" starts
// a new section; text before the first numbered heading is the intro.
function parseSections(text) {
  const lines = text.split("\n");
  const sections = [];
  let intro = [];
  let current = null;
  for (const raw of lines) {
    const line = raw;
    const m = line.match(/^(\d+)\.\s+(.*)$/);
    if (m) {
      if (current) sections.push(current);
      current = { heading: `${m[1]}. ${m[2]}`, body: [] };
    } else if (current) {
      current.body.push(line);
    } else {
      intro.push(line);
    }
  }
  if (current) sections.push(current);
  return { intro: intro.join("\n").trim(), sections };
}

export default function LegalDoc({ title, slug }) {
  const doc = LEGAL_DOCS[slug];

  return (
    <Layout page={slug}>
      <section className="relative pt-40 pb-12 overflow-hidden">
        <div className="absolute inset-0 mesh-top" />
        <div className="relative mx-auto max-w-3xl px-5 sm:px-8">
          <span className="label">Legal</span>
          <h1 className="font-display font-black text-white tracking-tighter mt-4 leading-[0.98]" style={{ fontSize: "clamp(2.2rem, 6vw, 4.4rem)" }}>{doc?.title || title}</h1>
          {doc?.subtitle && <p className="text-ice/70 mt-4 text-base sm:text-lg max-w-2xl">{doc.subtitle}</p>}
          {doc?.updated && <p className="text-mist text-xs mt-3">Last updated: {doc.updated}</p>}
        </div>
      </section>

      <section className="relative py-6 pb-32">
        <div className="mx-auto max-w-3xl px-5 sm:px-8" data-testid={`legaldoc-${slug}`}>
          {!doc ? (
            <div className="rounded-2xl border border-dashed border-white/15 bg-ink-800/50 p-7">
              <div className="flex items-center gap-3">
                <AlertCircle size={18} className="text-cyan" />
                <h3 className="font-display text-xl font-bold text-white">{title}</h3>
                <span className="label text-cyan ml-auto">Coming soon</span>
              </div>
              <p className="text-mist mt-3 text-sm leading-relaxed">This document is being finalised and will be published here shortly.</p>
            </div>
          ) : (() => {
            const { intro, sections } = parseSections(doc.text);
            return (
              <div className="space-y-5">
                {intro && (
                  <p className="text-ice/80 leading-relaxed whitespace-pre-line rounded-2xl border border-white/10 bg-ink-800/60 p-6">{intro}</p>
                )}
                {sections.map((s, i) => (
                  <div key={i} className="rounded-2xl border border-white/8 bg-ink-800/40 p-6 sm:p-7">
                    <h2 className="font-display text-lg sm:text-xl font-bold text-white mb-3">{s.heading}</h2>
                    <p className="text-ice/75 leading-relaxed text-sm whitespace-pre-line">{s.body.join("\n").trim()}</p>
                  </div>
                ))}
              </div>
            );
          })()}
        </div>
      </section>
    </Layout>
  );
}
